import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Mail, Phone, MapPin, Instagram, Twitter, Linkedin } from 'lucide-react';

const Contact = () => {
    const [contact, setContact] = useState(null);

    useEffect(() => {
        const fetchContent = async () => {
            try {
                const { data } = await api.get('/sections/Contact');
                if (data && data.content && data.content.length > 0) {
                    const contentMap = {};
                    data.content.forEach(item => contentMap[item.key] = item.value);
                    setContact(contentMap);
                } else {
                    setContact({
                        email: "info@sncaitraining.space",
                        phone: "+265884228668",
                        address: "Blantyre",
                        instagram: "https://instagram.com",
                        twitter: "https://twitter.com",
                        linkedin: "https://linkedin.com"
                    });
                }
            } catch (error) {
                console.error("Failed to fetch Contact content", error);
                setContact({
                    email: "info@sncaitraining.space",
                    phone: "+265884228668",
                    address: "Blantyre",
                    instagram: "https://instagram.com",
                    twitter: "https://twitter.com",
                    linkedin: "https://linkedin.com"
                });
            }
        };
        fetchContent();
    }, []);

    if (!contact) return null;

    return (
        <section id="contact" className="common-padding bg-slate-50 dark:bg-black text-black dark:text-white transition-colors duration-500 overflow-hidden">
            <div className="screen-max-width">
                <div className="relative p-8 md:p-16 rounded-[2.5rem] bg-white/40 dark:bg-white/[0.02] border border-black/10 dark:border-white/[0.08] backdrop-blur-2xl shadow-2xl transition-all duration-500 group overflow-hidden">
                    {/* Background Decorative Glows */}
                    <div className="absolute -top-24 -right-24 w-64 h-64 bg-blue/10 rounded-full blur-[100px] pointer-events-none group-hover:bg-blue/20 transition-all duration-700" />
                    <div className="absolute -bottom-24 -left-24 w-64 h-64 bg-blue/10 rounded-full blur-[100px] pointer-events-none group-hover:bg-blue/20 transition-all duration-700" />

                    <div className="relative z-10 flex flex-col md:flex-row justify-between items-start gap-12">
                        <div className="flex-1">
                            <h2 className="text-3xl md:text-5xl font-bold mb-6">Get in Touch</h2>
                            <p className="text-gray-400 mb-8 max-w-md">
                                Have questions or want to work together? We'd love to hear from you.
                            </p>

                            <div className="flex flex-wrap gap-8">
                                {contact.email && (
                                    <div className="flex items-center gap-5 min-w-[280px] p-4 rounded-2xl bg-black/[0.03] dark:bg-white/[0.03] border border-black/5 dark:border-white/5 hover:bg-black/[0.05] dark:hover:bg-white/[0.08] transition-all group/item">
                                        <div className="w-12 h-12 rounded-xl bg-blue/10 flex items-center justify-center group-hover/item:scale-110 transition-transform">
                                            <Mail className="w-6 h-6 text-blue" />
                                        </div>
                                        <div>
                                            <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-widest font-bold mb-1">Email</p>
                                            <a href={`mailto:${contact.email}`} className="text-lg font-medium hover:text-blue transition-colors">{contact.email}</a>
                                        </div>
                                    </div>
                                )}

                                {contact.phone && (
                                    <div className="flex items-center gap-5 min-w-[280px] p-4 rounded-2xl bg-black/[0.03] dark:bg-white/[0.03] border border-black/5 dark:border-white/5 hover:bg-black/[0.05] dark:hover:bg-white/[0.08] transition-all group/item">
                                        <div className="w-12 h-12 rounded-xl bg-blue/10 flex items-center justify-center group-hover/item:scale-110 transition-transform">
                                            <Phone className="w-6 h-6 text-blue" />
                                        </div>
                                        <div>
                                            <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-widest font-bold mb-1">Phone</p>
                                            <a href={`tel:${contact.phone}`} className="text-lg font-medium hover:text-blue transition-colors">{contact.phone}</a>
                                        </div>
                                    </div>
                                )}

                                {contact.address && (
                                    <div className="flex items-center gap-5 min-w-[280px] p-4 rounded-2xl bg-black/[0.03] dark:bg-white/[0.03] border border-black/5 dark:border-white/5 hover:bg-black/[0.05] dark:hover:bg-white/[0.08] transition-all group/item">
                                        <div className="w-12 h-12 rounded-xl bg-blue/10 flex items-center justify-center group-hover/item:scale-110 transition-transform">
                                            <MapPin className="w-6 h-6 text-blue" />
                                        </div>
                                        <div>
                                            <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-widest font-bold mb-1">Address</p>
                                            <p className="text-lg font-medium whitespace-pre-line">{contact.address}</p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="w-full md:w-auto h-full flex flex-col justify-between">
                            <div>
                                <h3 className="text-xl font-bold mb-6 tracking-tight">Connect with us</h3>
                                <div className="flex gap-4">
                                    {contact.instagram && (
                                        <a href={contact.instagram} target="_blank" rel="noopener noreferrer" className="w-14 h-14 rounded-2xl bg-black/5 dark:bg-white/5 border border-black/10 dark:border-white/10 flex items-center justify-center hover:bg-blue hover:text-white dark:hover:bg-blue transition-all duration-300 hover:scale-110 group/social">
                                            <Instagram className="w-7 h-7" />
                                        </a>
                                    )}
                                    {contact.twitter && (
                                        <a href={contact.twitter} target="_blank" rel="noopener noreferrer" className="w-14 h-14 rounded-2xl bg-black/5 dark:bg-white/5 border border-black/10 dark:border-white/10 flex items-center justify-center hover:bg-blue hover:text-white dark:hover:bg-blue transition-all duration-300 hover:scale-110 group/social">
                                            <Twitter className="w-7 h-7" />
                                        </a>
                                    )}
                                    {contact.linkedin && (
                                        <a href={contact.linkedin} target="_blank" rel="noopener noreferrer" className="w-14 h-14 rounded-2xl bg-black/5 dark:bg-white/5 border border-black/10 dark:border-white/10 flex items-center justify-center hover:bg-blue hover:text-white dark:hover:bg-blue transition-all duration-300 hover:scale-110 group/social">
                                            <Linkedin className="w-7 h-7" />
                                        </a>
                                    )}
                                </div>
                            </div>

                            <div className="mt-12 hidden md:block">
                                <div className="p-1 rounded-full bg-blue animate-pulse w-2 h-2 inline-block mr-2" />
                                <span className="text-xs font-bold uppercase tracking-widest text-gray-500">Available 24/7 for you</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Contact;
